import {
  advanceAfterFeedback,
  buildSummary,
  eligibleMeanings,
  endSession,
  markConflict,
  markSaveFailed,
  markSaveSucceeded,
  practiseAgain,
  progressLabel,
  reportQuestionProblem,
  startSession,
  submitAnswer,
  validateSessionSize,
  type PracticeMode,
  type PracticeSelection,
  type PracticeSessionState,
  type SubmitAnswerInput,
} from "../../domain/practice/engine.ts";
import {
  buildCardSummary,
  closeCardHistory,
  continueAfterResolution,
  eligibleCardMeanings,
  endCardSession,
  failCardSave,
  gradeCard,
  noteCardCommit,
  openCardHistory,
  previousCard,
  resumeCard,
  revealCard,
  showCardContext,
  skipCard,
  startCardSession,
  type CardSessionState,
  type CardSummary,
} from "../../domain/practice/cards.ts";
import type { CardDirection, CardGradeOutcome, CardReviewEvent, PracticeFormat, VocabularySnapshot } from "../../domain/vocabulary/types.ts";
import { applyReviewEvent, restorePreAssessmentSchedule } from "../../domain/practice/events.ts";
import { createObservableStore } from "../observable.ts";
import type { Platform } from "../ports/platform.ts";
import type { VocabularyStore } from "../ports/storage.ts";
import { ProductError } from "../../contracts/errors.ts";
import { SCHEDULER_POLICY_VERSION } from "../../domain/vocabulary/types.ts";
import { practiceStateFor, scheduleSnapshotOf } from "../../domain/practice/scheduling.ts";
import type { ReviewEvent, WrittenReviewEvent } from "../../domain/vocabulary/types.ts";

export interface PracticeSetupState {
  mode: PracticeMode;
  format: PracticeFormat;
  direction: CardDirection;
  size: number;
  selectedMeaningIds: string[];
}

export interface PracticeControllerState {
  setup: PracticeSetupState;
  session?: PracticeSessionState;
  cardSession?: CardSessionState;
  warning?: string;
  nextReviewAt?: string;
}

export class PracticeController {
  private readonly store = createObservableStore<PracticeControllerState>({
    setup: { mode: "due", format: "written", direction: "hide_meaning", size: 20, selectedMeaningIds: [] },
  });

  constructor(
    private readonly vocabulary: VocabularyStore,
    private readonly platform: Platform,
  ) {
    this.vocabulary.subscribeStatus((status) => {
      if (status.kind === "ready" && status.origin === "replace") {
        this.clearSession();
        return;
      }
      if (status.kind !== "ready" && status.kind !== "empty") {
        this.clearSession();
      }
    });
    this.vocabulary.subscribe((snapshot) => {
      this.invalidateActiveCard(snapshot);
    });
  }

  subscribe = this.store.subscribe.bind(this.store);
  getSnapshot = this.store.getSnapshot.bind(this.store);

  now(): string {
    return this.platform.clock.now();
  }

  eligibleCount(mode?: PracticeMode): number {
    const snapshot = this.vocabulary.getSnapshot();
    if (!snapshot) {
      return 0;
    }
    const setup = this.store.getSnapshot().setup;
    const selectedMode = mode ?? setup.mode;
    if (setup.format === "cards") {
      return eligibleCardMeanings(snapshot, selectedMode, setup.direction, setup.selectedMeaningIds, this.now()).length;
    }
    return eligibleMeanings(snapshot, selectedMode, setup.selectedMeaningIds, this.now()).length;
  }

  setFormat(format: PracticeFormat): void {
    this.store.setState((current) => ({
      ...current,
      setup: { ...current.setup, format },
      warning: undefined,
    }));
  }

  setDirection(direction: CardDirection): void {
    this.store.setState((current) => ({
      ...current,
      setup: { ...current.setup, direction },
      warning: undefined,
    }));
  }

  setMode(mode: PracticeMode): void {
    this.store.setState((current) => ({
      ...current,
      setup: { ...current.setup, mode },
      warning: undefined,
    }));
  }

  setSize(size: number): void {
    this.store.setState((current) => ({
      ...current,
      setup: { ...current.setup, size },
      warning: undefined,
    }));
  }

  setSelectedMeanings(ids: string[]): void {
    this.store.setState((current) => ({
      ...current,
      setup: { ...current.setup, selectedMeaningIds: ids },
      warning: undefined,
    }));
  }

  start(): boolean {
    const snapshot = this.vocabulary.getSnapshot();
    const setup = this.store.getSnapshot().setup;
    if (!snapshot) {
      this.store.setState((current) => ({
        ...current,
        warning: "Vocabulary is not available.",
      }));
      return false;
    }
    const sizeCheck = validateSessionSize(setup.size);
    if (!sizeCheck.ok) {
      this.store.setState((current) => ({ ...current, warning: sizeCheck.message }));
      return false;
    }
    if (setup.format === "cards") {
      const started = startCardSession(
        snapshot,
        {
          mode: setup.mode,
          direction: setup.direction,
          size: setup.size,
          selectedMeaningIds: setup.selectedMeaningIds,
        },
        this.deps(),
      );
      if (!started.ok) {
        this.store.setState((current) => ({
          ...current,
          warning: started.message,
          nextReviewAt: started.nextReviewAt,
        }));
        return false;
      }
      this.store.setState((current) => ({
        ...current,
        session: undefined,
        cardSession: started.session,
        warning: undefined,
        nextReviewAt: undefined,
      }));
      void this.persistCardEvent(started.event);
      return true;
    }
    const started = startSession(
      snapshot,
      {
        mode: setup.mode,
        size: setup.size,
        selectedMeaningIds: setup.selectedMeaningIds,
      } satisfies PracticeSelection,
      this.deps(),
    );
    if (!started.ok) {
      this.store.setState((current) => ({
        ...current,
        warning: started.message,
        nextReviewAt: started.nextReviewAt,
      }));
      return false;
    }
    this.store.setState((current) => ({
      ...current,
      session: started.session,
      cardSession: undefined,
      warning: undefined,
      nextReviewAt: undefined,
    }));
    return true;
  }

  async reveal(): Promise<void> {
    await this.runCardCommand((snapshot, session) => revealCard(snapshot, session, this.deps()));
  }

  async showContext(): Promise<void> {
    await this.runCardCommand((snapshot, session) => showCardContext(snapshot, session, this.deps()));
  }

  async grade(outcome: CardGradeOutcome): Promise<void> {
    const current = this.store.getSnapshot().cardSession;
    const item = current?.items[current.cursor];
    if (
      current &&
      item &&
      item.result === outcome &&
      current.status === "active" &&
      current.phase !== "saving" &&
      current.phase !== "save_failed" &&
      current.phase !== "conflict" &&
      current.phase !== "clock_blocked"
    ) {
      const snapshot = this.vocabulary.getSnapshot();
      if (!snapshot) {
        return;
      }
      const advanced = continueAfterResolution(snapshot, current, this.deps());
      this.store.setState((state) => ({ ...state, cardSession: advanced.session }));
      if (advanced.event) {
        await this.persistCardEvent(advanced.event);
      }
      return;
    }
    await this.runCardCommand((snapshot, session) => gradeCard(snapshot, session, outcome, this.deps()));
  }

  async skip(): Promise<void> {
    await this.runCardCommand((snapshot, session) => skipCard(snapshot, session, this.deps()));
  }

  async previous(): Promise<void> {
    await this.runCardCommand((snapshot, session) => previousCard(snapshot, session, this.deps()));
  }

  async resume(): Promise<void> {
    await this.runCardCommand((snapshot, session) => resumeCard(snapshot, session, this.deps()));
  }

  async retryCardSave(): Promise<void> {
    const event = this.store.getSnapshot().cardSession?.pendingEvent;
    const phase = this.store.getSnapshot().cardSession?.phase;
    if (!event || phase !== "save_failed") {
      return;
    }
    this.store.setState((current) =>
      current.cardSession
        ? { ...current, cardSession: { ...current.cardSession, phase: "saving", persistError: undefined } }
        : current,
    );
    await this.persistCardEvent(event);
  }

  async submit(input: SubmitAnswerInput): Promise<void> {
    const current = this.store.getSnapshot();
    const snapshot = this.vocabulary.getSnapshot();
    if (!current.session || !snapshot || current.session.phase !== "question") {
      return;
    }
    const result = submitAnswer(snapshot, current.session, input, this.deps());
    this.store.setState({
      ...current,
      session: result.session,
    });
    if (result.session.phase === "clock_blocked" || !result.event) {
      return;
    }
    await this.persistEvent(result.event, result.session);
  }

  async retrySave(): Promise<void> {
    const current = this.store.getSnapshot();
    const event = current.session?.pendingEvent;
    if (!current.session || !event || current.session.phase !== "save_failed") {
      return;
    }
    this.store.setState({
      ...current,
      session: { ...current.session, phase: "saving", persistError: undefined },
    });
    await this.persistEvent(event, { ...current.session, phase: "saving", persistError: undefined });
  }

  async reportQuestionProblem(): Promise<void> {
    const current = this.store.getSnapshot();
    const snapshot = this.vocabulary.getSnapshot();
    if (!current.session || !snapshot) {
      return;
    }
    const result = reportQuestionProblem(snapshot, current.session, this.deps());
    this.store.setState({ ...current, session: result.session });
    if (!result.event) {
      return;
    }
    await this.persistCorrection(result.event, result.session, result.correctionOfEventId);
  }

  next(): void {
    const current = this.store.getSnapshot();
    const snapshot = this.vocabulary.getSnapshot();
    if (!current.session || !snapshot || current.session.phase !== "feedback") {
      return;
    }
    const session = advanceAfterFeedback(snapshot, current.session, this.deps());
    this.store.setState({ ...current, session });
  }

  practiseAgain(): void {
    const current = this.store.getSnapshot();
    const snapshot = this.vocabulary.getSnapshot();
    if (!current.session || !snapshot) {
      return;
    }
    const session = practiseAgain(snapshot, current.session, this.deps());
    this.store.setState({ ...current, session });
  }

  end(explicit = true): void {
    const current = this.store.getSnapshot();
    if (current.cardSession) {
      if (current.cardSession.phase === "saving") {
        return;
      }
      this.store.setState({
        ...current,
        cardSession: endCardSession(current.cardSession, explicit),
      });
      return;
    }
    if (!current.session) {
      return;
    }
    this.store.setState({
      ...current,
      session: endSession(current.session, explicit),
    });
  }

  clearSession(): void {
    this.store.setState((current) => ({ ...current, session: undefined, cardSession: undefined }));
  }

  browseCard(index: number): void {
    const current = this.store.getSnapshot();
    if (!current.cardSession) {
      return;
    }
    this.store.setState({
      ...current,
      cardSession: openCardHistory(current.cardSession, index),
    });
  }

  closeCardBrowse(): void {
    const current = this.store.getSnapshot();
    if (!current.cardSession) {
      return;
    }
    this.store.setState({
      ...current,
      cardSession: closeCardHistory(current.cardSession),
    });
  }

  summary() {
    const snapshot = this.vocabulary.getSnapshot();
    const session = this.store.getSnapshot().session;
    if (!snapshot || !session) {
      return undefined;
    }
    return buildSummary(snapshot, session);
  }

  cardSummary(): CardSummary | undefined {
    const session = this.store.getSnapshot().cardSession;
    if (!session) {
      return undefined;
    }
    return buildCardSummary(session);
  }

  progress() {
    const session = this.store.getSnapshot().session;
    return session ? progressLabel(session) : undefined;
  }

  private deps() {
    return {
      random: () => this.platform.random.next(),
      createId: () => this.platform.ids.create(),
      now: () => this.platform.clock.now(),
    };
  }

  private relatedExposureEvents(parent: WrittenReviewEvent, snapshot: NonNullable<ReturnType<VocabularyStore["getSnapshot"]>>): WrittenReviewEvent[] {
    const question = this.store.getSnapshot().session?.current;
    if (!question || (parent.eventKind === "invalidation" && parent.firstAttemptOutcome == null)) {
      return [];
    }
    const related = new Set<string>();
    for (const id of question.exercise.referencedMeaningIds) {
      if (id !== parent.meaningId) {
        related.add(id);
      }
    }
    for (const distinction of question.exercise.relatedWordDistinctions) {
      related.add(distinction.meaningId);
    }
    const events: WrittenReviewEvent[] = [];
    for (const meaningId of related) {
      const meaning = snapshot.meanings.find((item) => item.id === meaningId);
      if (!meaning) {
        continue;
      }
      const state = practiceStateFor(snapshot, meaningId);
      events.push({
        format: "written",
        origin: parent.exerciseId ? "exercise" : "unspecified",
        eventId: `${parent.eventId}:exposure:${meaningId}`,
        sessionId: parent.sessionId,
        meaningId,
        exerciseId: parent.exerciseId,
        exerciseKind: parent.exerciseKind,
        meaningContentRevision: meaning.contentRevision,
        expectedStateRevision: state.stateRevision,
        presentedAt: parent.completedAt,
        completedAt: parent.completedAt,
        eventKind: "exposure",
        firstAttemptOutcome: null,
        hintUsed: false,
        scheduledOrExtra: "extra",
        schedulerVersion: SCHEDULER_POLICY_VERSION,
        previousSchedule: scheduleSnapshotOf(state),
        resultingSchedule: scheduleSnapshotOf({
          ...state,
          lastExposureAt: parent.completedAt,
          stateRevision: state.stateRevision + 1,
        }),
      });
    }
    return events;
  }

  private async persistEvent(event: WrittenReviewEvent, session: PracticeSessionState): Promise<void> {
    const commit = await this.vocabulary.commit((latest) => {
      const applied = applyReviewEvent(latest, event);
      if (!applied.ok) {
        throw conflictError(applied.reason);
      }
      let next = applied.snapshot;
      for (const related of this.relatedExposureEvents(event, latest)) {
        const relatedApplied = applyReviewEvent(next, {
          ...related,
          expectedStateRevision: practiceStateFor(next, related.meaningId).stateRevision,
        });
        if (relatedApplied.ok) {
          next = relatedApplied.snapshot;
        }
      }
      return next;
    });
    this.applyCommitResult(commit, session);
  }

  private async persistCorrection(
    event: ReviewEvent,
    session: PracticeSessionState,
    assessmentEventId?: string,
  ): Promise<void> {
    const commit = await this.vocabulary.commit((latest) => {
      if (assessmentEventId) {
        const restored = restorePreAssessmentSchedule(latest, assessmentEventId, event);
        if (!restored.ok) {
          throw conflictError(restored.reason);
        }
        return restored.snapshot;
      }
      const applied = applyReviewEvent(latest, event);
      if (!applied.ok) {
        throw conflictError(applied.reason);
      }
      return applied.snapshot;
    });
    this.applyCommitResult(commit, session);
  }

  private applyCommitResult(
    commit: Awaited<ReturnType<VocabularyStore["commit"]>>,
    session: PracticeSessionState,
  ): void {
    if (!commit.ok) {
      const failed =
        commit.code === "stale_revision"
          ? markConflict(session, commit.message)
          : markSaveFailed(session, commit.message);
      this.store.setState((state) => ({
        ...state,
        session: state.session ? { ...failed, pendingEvent: state.session.pendingEvent } : failed,
      }));
      return;
    }
    this.store.setState((state) => ({
      ...state,
      session: state.session ? markSaveSucceeded(state.session, commit.snapshot) : state.session,
    }));
  }

  private async runCardCommand(
    command: (
      snapshot: VocabularySnapshot,
      session: CardSessionState,
    ) => { session: CardSessionState; event?: CardReviewEvent },
  ): Promise<void> {
    const current = this.store.getSnapshot();
    const snapshot = this.vocabulary.getSnapshot();
    if (!current.cardSession || !snapshot || current.cardSession.phase === "saving") {
      return;
    }
    const result = command(snapshot, current.cardSession);
    this.store.setState((state) => ({ ...state, cardSession: result.session }));
    if (result.event) {
      await this.persistCardEvent(result.event);
    }
  }

  private async persistCardEvent(event: CardReviewEvent): Promise<void> {
    const commit = await this.vocabulary.commit((latest) => {
      const applied = applyReviewEvent(latest, event);
      if (!applied.ok) {
        throw conflictError(applied.reason);
      }
      return applied.snapshot;
    });
    if (!commit.ok) {
      this.store.setState((state) => {
        if (!state.cardSession || state.cardSession.pendingEvent?.eventId !== event.eventId) {
          return state;
        }
        if (state.cardSession.phase === "conflict") {
          return state;
        }
        const kind =
          commit.code === "validation_failed"
            ? "clock_blocked"
            : commit.code === "stale_revision"
              ? "conflict"
              : "save_failed";
        return { ...state, cardSession: failCardSave(state.cardSession, commit.message, kind) };
      });
      return;
    }
    const current = this.store.getSnapshot().cardSession;
    if (!current || current.pendingEvent?.eventId !== event.eventId) {
      return;
    }
    const noted = noteCardCommit(current, event, commit.snapshot);
    const advanced =
      event.actionKind === "grade" || event.actionKind === "skip"
        ? continueAfterResolution(commit.snapshot, noted, this.deps())
        : { session: noted, event: undefined as CardReviewEvent | undefined };
    this.store.setState((state) => {
      if (!state.cardSession || state.cardSession.pendingEvent?.eventId !== event.eventId) {
        return state;
      }
      return { ...state, cardSession: advanced.session };
    });
    if (advanced.event) {
      await this.persistCardEvent(advanced.event);
    }
  }

  private invalidateActiveCard(snapshot: VocabularySnapshot): void {
    const session = this.store.getSnapshot().cardSession;
    if (!session || session.status !== "active" || session.phase === "saving") {
      return;
    }
    const item = session.items[session.cursor];
    if (!item || item.result != null || session.cursor !== session.items.length - 1) {
      return;
    }
    const meaning = snapshot.meanings.find((entry) => entry.id === item.meaningId);
    if (meaning && meaning.contentRevision === item.contentRevision) {
      return;
    }
    this.store.setState((state) => {
      if (!state.cardSession || state.cardSession.status !== "active") {
        return state;
      }
      return {
        ...state,
        cardSession: {
          ...state.cardSession,
          phase: "conflict",
          conflictMessage: meaning
            ? "This card changed and can no longer be graded. End the session and start again."
            : "This meaning was deleted. End the session to continue.",
        },
      };
    });
  }
}

function conflictError(reason: string): ProductError {
  if (reason === "clock_backward") {
    return new ProductError(
      "validation_failed",
      "This device clock moved backwards. Practice results are paused until the time looks consistent.",
      true,
    );
  }
  if (
    reason === "missing_meaning" ||
    reason === "stale_content" ||
    reason === "missing_exercise" ||
    reason === "stale_classification" ||
    reason === "causal_mismatch"
  ) {
    return new ProductError(
      "stale_revision",
      "This question is no longer valid because vocabulary changed. Restart practice with the current words.",
    );
  }
  return new ProductError(
    "stale_revision",
    "This result could not be saved because the schedule changed in another window.",
  );
}

export type { PracticeSessionState, CardSessionState, CardSummary };
